import { FC, type CSSProperties } from 'react';
import paulinaAvatar from '@assets/images/paulina-avatar.jpg';
import { V4, Z } from '@components/v4';

/**
 * PaulinaPill — entrada de Paulina en DESKTOP (Opción A). z-index `Z.paulinaPill` (80, SIEMPRE por
 * debajo del toast de pedido `Z.pedido`=99 en NotificationCenterV2).
 *
 * COEXISTE, no se oculta (Track 1): la pill NUNCA se desmonta cuando hay un pedido entrante o el
 * detalle abierto. En su lugar CEDE (`yielding`): colapsa a solo-avatar (sin la etiqueta "Paulina")
 * y se reubica abajo-IZQUIERDA (`left`), fuera del toast (abajo-derecha) y de las acciones del
 * detalle. Al dejar de ceder vuelve a la pill NOMBRADA abajo-derecha. Lo operativo sigue ganando: el
 * toast (z-99) queda por encima de la pill (z-80) en todos los casos.
 *
 * Estructura: contenedor externo = posición fija + desplazamiento entre esquinas (un solo `transform`
 * → transición suave); botón interno = visual de la pill + `data-press` (micro-feedback de presión del
 * design system V4, `transform: scale` en `:active`) — separados para que el scale del press NO pise
 * el translate de la reubicación. Toda la animación (deslizamiento, colapso de la etiqueta) respeta
 * `prefers-reduced-motion`: solo corre bajo `no-preference`; con motion reducido el cambio es instantáneo.
 *
 * Estilos 100% inline con tokens V4 (PRJ-004), cero Tailwind. El dot de nudge (`V4.accentMint`, SOLO
 * dot sin texto — WCAG) se muestra cuando hay un nudge pendiente y la pill NO está cediendo.
 */

interface PaulinaPillProps {
  hasNudge: boolean;
  /** Modo cedido: colapsa a solo-avatar y se reubica abajo-izquierda (pedido entrante / detalle). */
  yielding?: boolean;
  onOpen: () => void;
}

// Transiciones bajo `prefers-reduced-motion: no-preference` (inyección idempotente en <head>, mismo
// patrón que las demás superficies de Paulina). Con motion reducido las clases quedan inertes → el
// deslizamiento entre esquinas y el colapso de la etiqueta ocurren sin animación.
const KF_ID = 'plick-paulina-pill-kf';
if (typeof document !== 'undefined' && !document.getElementById(KF_ID)) {
  const st = document.createElement('style');
  st.id = KF_ID;
  st.textContent =
    '@media (prefers-reduced-motion: no-preference){' +
    '.plick-paulina-pill-anchor{transition:transform 260ms cubic-bezier(.2,.8,.2,1)}' +
    '.plick-paulina-pill-btn{transition:gap 200ms ease,padding 220ms ease}' +
    '.plick-paulina-pill-label{transition:max-width 220ms ease,opacity 150ms ease}}';
  document.head.appendChild(st);
}

export const PaulinaPill: FC<PaulinaPillProps> = ({ hasNudge, yielding = false, onOpen }) => {
  const compact = yielding;

  // Contenedor externo: ancla abajo-izquierda + desplazamiento a la esquina destino con un solo
  // `transform`. Cedido → `translateX(0)` (abajo-izquierda). Sin ceder → se desliza a abajo-derecha
  // (right:24px equivalente: 100vw − ancho propio − (24+24)). El `100%` resuelve al ancho del propio
  // contenedor, así que la esquina derecha queda a 24px del borde sin importar el ancho de la pill.
  const anchor: CSSProperties = {
    position: 'fixed',
    bottom: 24,
    left: 24,
    zIndex: Z.paulinaPill,
    transform: compact ? 'translateX(0)' : 'translateX(calc(100vw - 100% - 48px))',
  };

  const button: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: compact ? 0 : 10,
    background: V4.primary,
    color: V4.white,
    border: 0,
    borderRadius: 9999,
    padding: compact ? 5 : '8px 16px 8px 8px',
    boxShadow: '0 12px 30px rgba(37,55,70,.30)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div className="plick-paulina-pill-anchor" style={anchor}>
      <button
        type="button"
        data-press
        aria-label="Abrir chat con Paulina"
        onClick={onOpen}
        className="plick-paulina-pill-btn"
        style={button}
      >
        <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <img
            src={paulinaAvatar}
            alt=""
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              objectFit: 'cover',
              border: `2px solid ${V4.secondary}`,
              display: 'block',
            }}
          />
          {hasNudge && !compact && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: V4.accentMint,
                border: `2px solid ${V4.primary}`,
              }}
            />
          )}
        </span>
        <span
          className="plick-paulina-pill-label"
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: compact ? 0 : 80,
            opacity: compact ? 0 : 1,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          Paulina
        </span>
      </button>
    </div>
  );
};
